import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";

import { selectSessionStaticData } from "../../store/reducers/sessionSlice";
import { RootState } from "../../store/store";
import { SessionStatus } from "../../types/session.definition";
import { getAdminUrl, getDashboardUrl } from "../../utils/config-utils";

const getRoleRedirectElement = (
  role: string | undefined,
  goToRoleHome: boolean,
  pathname: string,
  searchString: string
): React.ReactElement | null => {
  switch (role) {
    case "patient":
      return (
        <Navigate
          to={`${goToRoleHome ? "/profile" : pathname}${searchString}`}
          replace
        />
      );
    case "doctor":
      return (
        <Navigate
          to={`${goToRoleHome ? "/doctor/profile" : pathname}${searchString}`}
          replace
        />
      );
    case "manager":
    case "secretary": {
      const dashboardUrl = getDashboardUrl();
      if (dashboardUrl) {
        window.location.href = dashboardUrl;
      }
      return null;
    }
    case "admin":
    case "super_admin": {
      const adminUrl = getAdminUrl();
      if (adminUrl) {
        window.location.href = adminUrl;
      }
      return null;
    }
    default:
      return null;
  }
};

export const RedirectByRole: React.FC = () => {
  const location = useLocation();
  const { sessionStatus } = useSelector(selectSessionStaticData);
  const { session, loading } = useSelector((state: RootState) => state.session);

  // Get URL search params to check if we're coming from login.
  // Only "login" (set by the login-bff redirect) is a real login-flow
  // marker. Do NOT also match "sessionToken": it's a general-purpose deep-link
  // mechanism (see root CLAUDE.md "Session token can also be passed as URL
  // parameter for deep links") used to open a specific page (e.g.
  // /doctor/schedule?sessionToken=...) while authenticated — treating its
  // mere presence as "just logged in" hijacks that navigation to the role's
  // generic home page instead of the deep-linked page. Do NOT match a bare
  // "token" param either: payment gateway return URLs (e.g. PayDunya) also
  // append "?token=..." on redirect, which would otherwise hijack
  // post-payment navigation to the role's home page.
  const searchParams = new URLSearchParams(location.search);
  const isHomePage = location.pathname === "/";
  // A login hands the user over on "/?sessionToken=…" (login-bff's main app
  // URL): that landing goes to the role's home. On any other page the token is
  // a deep link (see above). SessionProvider strips the token from the address
  // bar with history.replaceState, which the router does not see, so the first
  // render after the session check still has it.
  const isLoginHandoff = isHomePage && searchParams.has("sessionToken");
  const justLoggedIn = searchParams.has("login") || isLoginHandoff;
  const isLoggedIn =
    sessionStatus === SessionStatus.VALID_SESSION && session?.user;

  // Don't redirect while still loading the session
  if (loading) {
    return null;
  }

  if (!isLoggedIn) {
    return null;
  }

  const role = session?.user?.roleName?.toLowerCase();
  // Patients and doctors may open the public home ("/") while signed in, e.g.
  // to search (MED-1656); roles without pages in this app still leave for their own.
  const belongsElsewhere =
    Boolean(role) && !["patient", "doctor"].includes(role);

  if (!(justLoggedIn || (isHomePage && belongsElsewhere))) {
    return null;
  }

  // Create a clean version of the URL that excludes login-related parameters
  const cleanSearch = new URLSearchParams();
  searchParams.forEach((value, key) => {
    if (key !== "login" && key !== "sessionToken") {
      cleanSearch.append(key, value);
    }
  });

  const searchString = cleanSearch.toString()
    ? `?${cleanSearch.toString()}`
    : "";

  return getRoleRedirectElement(
    role,
    isHomePage || justLoggedIn,
    location.pathname,
    searchString
  );
};
